import React, { FC, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Badge, HIERARCHY } from "baseui/badge";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { Link } from "kanopi-components/Link";
import { navigate } from "@reach/router";
import { QuoteFilterBy } from "kw/api/zeus";
import { TableView } from "kanopi-components/TableView/TableView";
import { useFormatter } from "kw/util/useFormatter";
import { useQuotes } from "./hooks/useQuotes";
import { useUser } from "kw/auth/useUser";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import useQuoteStatus from "./hooks/useQuoteStatus";
import { useGate } from "statsig-react";
import { COLOR } from "baseui/badge";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { getSupportSought } from "kw/util/Client/accolade";
import { capitalize } from "kanopi-components/utils";

export const QuotesPage: FC = () => {
  const { quotes, loading, fetchQuotes, searchQuotes } = useQuotes();
  const { formatDate, formatCurrency, formatString } = useFormatter();
  const user = useUser();
  const [css, theme] = useStyletron();

  const handleSearch = (searchText: string, filterBy?: QuoteFilterBy) => {
    searchQuotes(searchText, filterBy ? filterBy : QuoteFilterBy.ALL);
  };
  const handlePagination = (search: string, filterBy: QuoteFilterBy, page?: number) => {
    fetchQuotes(search, filterBy ? filterBy : QuoteFilterBy.ALL, page ? page : 1);
  };

  const { value: isAccolade, isLoading } = useGate("accolade_admin_titles");

  const columnSchema = useMemo(() => {
    const common = [
      {
        header: "Quote ID",
        id: "id",
        children: (row: TQuote) => (
          <Link
            onClick={() => navigate(`/admin/quotes/${row.id}`)}
            data-testid={`rowId-${row.id}`}
          >
            {row.id}
          </Link>
        ),
        sortable: false,
      },
    ];

    if (isAccolade) {
      const accoladeStatusColours = {
        Received: COLOR.primary,
        Reviewing: COLOR.accent,
        Quoted: COLOR.positive,
        Declined: COLOR.negative,
        "Shut down": COLOR.warning,
        NTU: COLOR.warning,
        Bound: COLOR.primary,
      };

      return [
        ...common,
        {
          header: "Original Insured (Abbrev.)",
          id: "originalInsuredAbbrev",
          children: (row: TQuote) =>
            formatString(row.riskProfile?.riskData?.originalInsuredAbbreviated ?? "-"),
          sortable: false,
        },
        {
          header: "Inception",
          id: "inception",
          children: (row: TQuote) => formatDate(row?.startDate),
          sortable: false,
        },
        {
          header: "Expiration",
          id: "expiration",
          children: (row: TQuote) => formatDate(row?.finishDate),
          sortable: false,
        },
        {
          header: "Broker",
          id: "broker",
          children: (row: TQuote) =>
            formatString(row.riskProfile?.riskData?.reinsuranceBroker ?? "-"),
          sortable: false,
        },
        {
          header: "Cedent",
          id: "cedent",
          children: (row: TQuote) =>
            formatString(row.riskProfile?.riskData?.cedent ?? "-"),
          sortable: false,
        },
        {
          header: "Support Sought",
          id: "supportSought",
          children: (row: TQuote) =>
            getSupportSought({
              stretch: row?.riskProfile?.riskData?.stretch,
              attachment: row?.riskProfile?.riskData?.attachmentPoint,
              structureType: row?.riskProfile?.riskData?.structureType,
            }),
          sortable: false,
        },
        {
          header: "Limit Operation",
          id: "limitOperation",
          children: (row: TQuote) =>
            row?.riskProfile?.riskData?.policyLimitOperation
              ? capitalize(row?.riskProfile?.riskData?.policyLimitOperation)
              : "-",
          sortable: false,
        },
        {
          header: "Line Size",
          id: "lineSize",
          children: (row: TQuote) =>
            ["Received", "Reviewing"].includes(row.riskProfile?.riskData?.status)
              ? formatString(
                  row?.riskProfile?.riskData?.lineSizeSoughtPercent
                    ? `${row?.riskProfile?.riskData?.lineSizeSoughtPercent}%`
                    : "-"
                )
              : formatString(
                  row?.riskProfile?.riskData?.totalLineSize
                    ? `${row?.riskProfile?.riskData?.totalLineSize}%`
                    : "-"
                ),
          sortable: false,
        },
        {
          header: "Potential Premium",
          id: "potentialPremium",
          children: (row: TQuote) =>
            formatCurrency(row.riskProfile?.riskData?.brokerTargetPremium ?? "-"),
          sortable: false,
        },
        {
          header: "Status",
          id: "status",
          children: (row: TQuote) => {
            if (!row.riskProfile?.riskData?.status) {
              return "-";
            }
            return (
              <Badge
                hierarchy={HIERARCHY.secondary}
                content={row.riskProfile?.riskData?.status}
                color={accoladeStatusColours[row.riskProfile?.riskData?.status]}
              />
            );
          },
          sortable: false,
        },
        {
          header: "Countdown",
          id: "countdown",
          children: (row: TQuote) => {
            if (!row.startDate) {
              return "-";
            }
            const startDate = new Date(row.startDate);
            const countdown = Math.ceil(
              (startDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
            );

            if (countdown <= 0) {
              return "-";
            } else if (countdown < 5) {
              return (
                <span
                  className={css({
                    color: theme.colors.negative,
                  })}
                >
                  {countdown}
                </span>
              );
            }
            return countdown;
          },
          sortable: false,
        },
        {
          header: "Free Text",
          id: "freeText",
          children: (row: TQuote) =>
            formatString(row?.riskProfile?.riskData?.freeText ?? "-"),
          sortable: false,
        },
      ];
    }

    return [
      ...common,
      {
        header: "Customer",
        id: "customer",
        children: (row: TQuote) =>
          formatString(
            `${row.riskProfile?.owner?.firstName} ${row.riskProfile?.owner?.lastName}`.trim()
          ),
        sortable: false,
      },
      {
        header: "Start date",
        id: "startDate",
        children: (row: TQuote) => formatDate(row?.startDate),
        sortable: false,
      },
      {
        header: "Finish date",
        id: "finishDate",
        children: (row: TQuote) => formatDate(row?.finishDate),
        sortable: false,
      },
      {
        header: "Premium",
        id: "premium",
        children: (row: TQuote) => formatCurrency(row?.postTaxPremium),
        sortable: false,
      },
      {
        header: "Status",
        id: "status",
        children: (row: TQuote) => {
          const { caption, captionColor } = useQuoteStatus(row);
          return (
            <Badge
              hierarchy={HIERARCHY.secondary}
              content={caption}
              color={captionColor}
            />
          );
        },
        sortable: false,
      },
    ];
  }, [isAccolade]);

  useEffect(() => {
    fetchQuotes("", QuoteFilterBy.ALL, 1);
  }, []);

  return (
    <>
      <Helmet>
        <title>Quotes - {user?.organization?.name}</title>
      </Helmet>
      <VerticalSpacer>
        <HeadingActionsBar heading="Quotes" />
        <TableView
          warpInCard={true}
          columnSchema={columnSchema}
          data={quotes?.items || []}
          pageCount={quotes?.pageCount || 1}
          onPageChange={handlePagination}
          currentPage={quotes?.page || 1}
          loading={isLoading || loading}
          onSearch={handleSearch}
          searchPlaceholder="Search by ID or customer"
          type="QUOTE"
          showSearchOptions={true}
          searchOptions={[
            { label: "All", value: QuoteFilterBy.ALL },
            { label: "Draft", value: QuoteFilterBy.DRAFT },
            { label: "Quoted", value: QuoteFilterBy.QUOTED },
            { label: "Accepted", value: QuoteFilterBy.ACCEPTED },
            { label: "Submitted", value: QuoteFilterBy.SUBMITTED },
            { label: "Referred", value: QuoteFilterBy.REFERRED },
            { label: "Expired", value: QuoteFilterBy.EXPIRED },
          ]}
          filterPlaceholder="Filter by status"
        />
      </VerticalSpacer>
    </>
  );
};
